import type {
  AutopayEligibility,
  AutopayIneligibilityReason,
  HelocLoanInfo,
  LoanResponse,
  PaymentEligibility,
  PaymentEligibilityError,
} from '@ld.consumer-portal/models';
import { HelocDrawEligibilityEnum } from '@ld.consumer-portal/models';
import { STOP_CODE_BLOCKED } from './payment-eligibility.service';

const ERROR_CODE_ACTIVE_PAYMENT_COUNT = 'Error-NumberOfOnlineActivePayment';

export function isOnlyActivePaymentCountError(
  eligibility: PaymentEligibility | undefined,
): boolean {
  if (!eligibility?.errors || eligibility.errors.length === 0) return false;
  if (eligibility.errors.length !== 1) return false;

  const error = eligibility.errors[0];
  if (typeof error === 'string') {
    return error === ERROR_CODE_ACTIVE_PAYMENT_COUNT;
  }
  return (error as PaymentEligibilityError).errorCode === ERROR_CODE_ACTIVE_PAYMENT_COUNT;
}

const ERROR_CODE_ONE_DRAW_PER_DAY = 'OneDrawPerDayLimit';

export function deriveDrawEligibility(
  helocInfo: HelocLoanInfo | undefined,
  enableDrawsFallback: boolean,
): HelocDrawEligibilityEnum {
  const drawCheck = helocInfo?.eligibilityCheck?.find((ec) => ec.type === 'Draw');
  if (drawCheck) {
    if (drawCheck.eligibility.isEligible) {
      return HelocDrawEligibilityEnum.ELIGIBLE;
    }
    const errorCode = drawCheck.eligibility.errors?.[0]?.errorCode;
    if (errorCode === ERROR_CODE_ONE_DRAW_PER_DAY) {
      return HelocDrawEligibilityEnum.DAILY_LIMIT_REACHED;
    }
    return HelocDrawEligibilityEnum.INELIGIBLE;
  }
  return enableDrawsFallback !== false
    ? HelocDrawEligibilityEnum.ELIGIBLE
    : HelocDrawEligibilityEnum.INELIGIBLE;
}

export function isHelocInHouse(
  helocInfo: HelocLoanInfo | undefined,
  isHelocInHousePaymentEnabled: boolean,
): boolean {
  const isEligible = helocInfo?.isLoanStatusEligibleForInHouse === true;
  return isHelocInHousePaymentEnabled && isEligible;
}

export function shouldShowHelocAmountDue(
  helocInfo: HelocLoanInfo | undefined,
  accountStanding: string,
): boolean {
  if (helocInfo?.isBillGenerated) return true;
  const isPastDue = ['Past Due', 'Delinquent', 'Foreclosure'].includes(accountStanding);
  if (isPastDue) return true;
  return false;
}

export interface CheckStopCodesTranslations {
  badCheckStopCode: string;
  paidInFull: string;
  foreclosure: string;
  processStopCode: string;
}

export function checkStopCodesSimple(
  loan: LoanResponse['loan'],
  translations: CheckStopCodesTranslations,
): string[] {
  const errors: string[] = [];

  if (loan.badCheckStopCode && STOP_CODE_BLOCKED.badCheck.includes(loan.badCheckStopCode)) {
    errors.push(translations.badCheckStopCode);
  }

  if (loan.pifStopCode === STOP_CODE_BLOCKED.pif) {
    errors.push(translations.paidInFull);
  }

  if (loan.foreclosureStopCode === STOP_CODE_BLOCKED.foreclosure) {
    errors.push(translations.foreclosure);
  }

  if (loan.processStopCode && STOP_CODE_BLOCKED.process.includes(loan.processStopCode)) {
    errors.push(translations.processStopCode);
  }

  return errors;
}

export function computeSimpleAutopayEligibility(
  loanResponse: LoanResponse,
  isAutopayFeatureEnabled: boolean,
): AutopayEligibility {
  const loan = loanResponse.loan;
  const reasons: AutopayIneligibilityReason[] = [];

  const isAlreadyEnrolled = loan.isInhouseAutopayEnabled || loan.isRepayAutopayEnabled;

  const monthlyAllowed = loanResponse.recurringPaymentEligibility?.isEligibile === 'Allowed';
  const biweeklyAllowed = loanResponse.biweeklyRecurringPaymentEligibility?.isEligibile === 'Allowed';
  const semiMonthlyAllowed = loanResponse.semiMonthlyRecurringPaymentEligibility?.isEligibile === 'Allowed';
  if (!monthlyAllowed && !biweeklyAllowed && !semiMonthlyAllowed) {
    reasons.push('PROCESS_STOP_CODE');
  }

  if (!isAutopayFeatureEnabled) {
    const blockingProcessCodes = ['9', 'B', 'D', 'F', 'L', 'N', 'R'];
    if (loan.processStopCode && blockingProcessCodes.includes(loan.processStopCode)) {
      reasons.push('PROCESS_STOP_CODE');
    }

    const daysPastDueStatus = loan.daysPastDueStatus?.toLowerCase() ?? '';
    const isOnTimeOrPastDue =
      daysPastDueStatus === 'on time' ||
      daysPastDueStatus === 'on-time' ||
      daysPastDueStatus === 'ontime' ||
      daysPastDueStatus === 'past due' ||
      daysPastDueStatus === 'pastdue';
    if (!isOnTimeOrPastDue) {
      reasons.push('LOAN_DELINQUENT');
    }

    if (!loan.isRetainedLoan) {
      reasons.push('NOT_RETAINED_LOAN');
    }
  }

  return {
    isEligible: reasons.length === 0,
    isAlreadyEnrolled,
    ineligibilityReasons: reasons,
  };
}
